/**
 * /vinde-masina/{marca} — sell-intent brand pages (RO only).
 * A page is indexable only with enough real data: at least
 * SELL_BRAND_MIN_LISTINGS active car/SUV listings and at least
 * SELL_BRAND_MIN_MODELS models that each have enough listings for a
 * real valuation (>= SELL_BRAND_MODEL_MIN_LISTINGS, the same minimum the
 * valuation RPC uses). Below the threshold the page stays live but noindex.
 */
import { brandSlug } from "@/lib/landingPages";
import { convertWithRates, type FxRatesPerEur } from "@/lib/fxRates";
import { isEligibleCityListing } from "@/utils/cityArea";

export const SELL_BRAND_MIN_LISTINGS = 20;
export const SELL_BRAND_MIN_MODELS = 3;
export const SELL_BRAND_MODEL_MIN_LISTINGS = 3;
export const SELL_BRAND_TOP_MODELS = 5;

/** Brands that met the threshold at launch (2026-11-03). Volvo had 21 listings but only 2 models with >= 3, so it is out. */
export const SELL_BRAND_MAKES = [
  "BMW",
  "Mercedes-Benz",
  "Volkswagen",
  "MG",
  "Audi",
  "Ford",
  "Kia",
  "Renault",
  "Toyota",
  "Dacia",
  "Peugeot",
  "Skoda",
  "Opel",
] as const;

export const SELL_BRAND_SLUG_TO_MAKE: Record<string, string> = Object.fromEntries(
  SELL_BRAND_MAKES.map((make) => [brandSlug(make), make]),
);

export interface SellBrandModelStat {
  model: string;
  count: number;
  minEur: number | null;
  maxEur: number | null;
}

export interface SellBrandStats {
  make: string;
  count: number;
  models: SellBrandModelStat[];
  modelsWithValuation: number;
}

type Row = {
  status?: string | null;
  category_code?: string | null;
  model?: string | null;
  price?: number | null;
  currency?: string | null;
};

/** Pure stats from the same rows the page loader fetches; EUR ranges via fx_rates, never a fixed rate. */
export function sellBrandStats(make: string, rows: Row[], rates: FxRatesPerEur | null): SellBrandStats {
  const eligible = rows.filter(isEligibleCityListing);
  const byModel = new Map<string, { count: number; prices: number[] }>();
  for (const row of eligible) {
    const model = (row.model || "").trim();
    if (!model) continue;
    const entry = byModel.get(model) ?? { count: 0, prices: [] };
    entry.count += 1;
    if (typeof row.price === "number" && row.price > 0) {
      const eur = convertWithRates(row.price, row.currency || "EUR", "EUR", rates);
      if (eur != null && eur >= 300 && eur <= 500000) entry.prices.push(eur);
    }
    byModel.set(model, entry);
  }
  const withValuation = [...byModel.entries()].filter(([, v]) => v.count >= SELL_BRAND_MODEL_MIN_LISTINGS);
  const models: SellBrandModelStat[] = withValuation
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, SELL_BRAND_TOP_MODELS)
    .map(([model, v]) => ({
      model,
      count: v.count,
      minEur: v.prices.length ? Math.round(Math.min(...v.prices)) : null,
      maxEur: v.prices.length ? Math.round(Math.max(...v.prices)) : null,
    }));
  return { make, count: eligible.length, models, modelsWithValuation: withValuation.length };
}

export const sellBrandIndexable = (s: SellBrandStats | null | undefined) =>
  !!s && s.count >= SELL_BRAND_MIN_LISTINGS && s.modelsWithValuation >= SELL_BRAND_MIN_MODELS;

export const sellBrandCanonical = (make: string) => `https://roautomarket.ro/vinde-masina/${brandSlug(make)}`;

export function sellBrandSeo(s: SellBrandStats): { title: string; description: string } {
  const n = s.count.toLocaleString("ro-RO");
  return {
    title: `Vinde mașina ta ${s.make} | RO Auto Market`,
    description: `Evaluează gratuit mașina ta ${s.make} și publică anunțul în câteva minute. Acum sunt ${n} anunțuri active ${s.make} pe RO Auto Market.`,
  };
}
